import React, { useState, useEffect, useCallback } from "react";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import CoreTable from "../../../app/core/components/CoreTable/CoreTable";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import { useNavigate } from "react-router-dom";
import CoreModal from "../../../app/core/components/CoreModal/CoreModal";
import SchedaOperator from "../../common/SchedaOperatore/SchedaOperator";
import { doReportOperator } from "./gestisciStudioSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useDeleteCommercialistaMutation,
  useGetCommercialistaQuery,
  useSaveCommercialistaMutation,
} from "../../api/admin/gestisciCommercialistiApi";
import CoreButtonHoverIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonHoverIcon";
import { TABLE_ROWS_NUMBER } from "../../../utils/constants/tables";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import UserFilter from "../../../app/core/components/UserFilter/UserFilter";
import GestisciStudioFilter from "./GestisciStudioFilter";
import { useTranslation } from "react-i18next";
import { useGetRolesQuery } from "../../api/admin/rolesApi";

const GestisciStudioPage = () => {
  const { t } = useTranslation();
  const appConfig = useSelector((state) => state.app.appConfig);
  const [rows, setRows] = useState([]);
  const [operator, setOperator] = useState([]);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [user, setUser] = useState();
  const [debouncedInputUserValue, setDebouncedInputUserValue] = useState("");

  const [filter, setFilter] = useState({
    studioId: "",
    identityRoleId: "",
  });

  const [page, setPage] = useState({
    currentPage: 1,
    startRow: 0,
    endRow: TABLE_ROWS_NUMBER,
  });

  const dispatch = useDispatch();

  const { data, isLoading, isFetching, error, refetch } =
    useGetCommercialistaQuery({ page, filter, value: debouncedInputUserValue });

  const { data: rolesData } = useGetRolesQuery(filter);

  const [deleteCommercialista, { isLoading: isLoadingDelete }] =
    useDeleteCommercialistaMutation();
  const [saveCommercialista, { isLoading: isLoadingSave }] =
    useSaveCommercialistaMutation();

  const navigate = useNavigate();

  const getOperatorData = useCallback(async () => {
    const isValidation = data && data?.Data && data.Data.items;

    if (isValidation) {
      const operatorData = data.Data.items.map((el) => ({
        Id: el.id,
        Nome: el.name,
        Cognome: el.surname,
        Email: el.email,
        Ruolo: el.subRole,
      }));
      setRows(operatorData);
    }
  }, [data]);

  useEffect(() => {
    getOperatorData();
  }, [data, isLoading, error, getOperatorData]);

  const handleOpenUserModal = (value) => {
    setOperator(value);
    setOpenUserModal(true);
  };
  const handleCloseUserModal = () => setOpenUserModal(false);

  const handleDownload = () => {
    toast.info(t("HandleDownload"));
    dispatch(doReportOperator());
  };

  const handleRefecthDocuments = () => {
    refetch();
  };

  const columns = [t("Name"), t("Surname"), "Email", t("Role")];

  const actions = [
    {
      icon: <AssignmentIndRoundedIcon />,
      onClick: handleOpenUserModal,
      tooltip: t("StudioCard"),
    },
  ];

  if (isLoading) {
    return <CoreSpinner />;
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <h2>{`${t("ManageStudio")}`}</h2>

          {appConfig?.modules?.studio?.adminProfileRoles && (
            <GestisciStudioFilter
              rolesData={rolesData?.Data}
              filter={filter}
              setFilter={setFilter}
            />
          )}
        </div>

        <div className="flex flex-row items-center">
          <div className="w-4/12">
            <UserFilter
              selectedUser={user}
              setSelectedUser={setUser}
              usersList={data?.Data?.items}
              setDebouncedInputUserValue={setDebouncedInputUserValue}
              isUsersLoading={isLoading || isFetching}
              externalLabel={`${t("BriefSearchOperator")}`}
              externalPlaceholder={`${t("ExtSearchOperator")}`}
              externalNegativeLabel={t("NoOperatorFound")}
            />
          </div>
          <CoreButtonHoverIcon
            onClick={handleRefecthDocuments}
            className="text-blue-500 m-1 flex flex-row space-x-2 border"
            testo={t("Refresh")}
            icon={<RefreshRoundedIcon className={`fill-blue-500`} />}
          />
          <CoreButtonHoverIcon
            className="text-blue-700 m-1"
            icon={<AddBoxRoundedIcon className={`fill-blue-700`} />}
            testo={`${t("Operator")}`}
            onClick={() =>
              navigate(`${t("create")}-${t("operator")}`, {
                state: { operator: true },
              })
            }
          />
          <CoreButtonHoverIcon
            className={"text-green-500 m-1"}
            onClick={() => navigate(`/auth/business/${t("customize-studio")}`)}
            icon={
              <EditRoundedIcon fontSize="small" className="text-green-500" />
            }
            testo={t("Customize")}
          />
          <CoreButtonHoverIcon
            className={"m-1 text-darkgray"}
            icon={
              <DownloadRoundedIcon fontSize="small" className="text-darkgray" />
            }
            testo="Report"
            onClick={handleDownload}
          />
        </div>
      </div>

      <CoreTable
        rows={rows}
        columns={columns}
        button
        actions={actions}
        info={`${t("Operator")}`}
        page={page}
        setPage={setPage}
        isLoading={isLoading || isLoadingDelete || isLoadingSave || isFetching}
        totalPages={Math.ceil(data?.Data?.count / TABLE_ROWS_NUMBER)}
      />

      <CoreModal
        width={1000}
        height={800}
        state={openUserModal}
        onClose={handleCloseUserModal}
        component={
          <SchedaOperator
            operator={operator}
            saveCommercialista={saveCommercialista}
            isLoadingSave={isLoadingSave}
            deleteCommercialista={deleteCommercialista}
            onClose={handleCloseUserModal}
          />
        }
      />
    </>
  );
};

export default GestisciStudioPage;
